import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from '../lib/supabase';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(email);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We couldn\u0027t send the reset email. Double-check your email address and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-5">
        <div className="max-w-sm w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="font-sans text-2xl text-ink mb-2" style={{ fontWeight: 600 }}>
              Check your email
            </h2>
            <p className="text-sm text-ink-faint" style={{ fontWeight: 300 }}>
              We&apos;ve sent a password reset link to <strong className="text-ink">{email}</strong>
            </p>
          </div>
          <p className="text-xs text-ink-faint mb-4">
            Didn&apos;t receive it? Check your spam folder or{' '}
            <button onClick={() => { setSuccess(false); setEmail(''); }} className="text-rose hover:underline font-medium">
              try again
            </button>.
          </p>
          <Link to="/" className="btn-secondary inline-block text-sm">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <h2 className="font-sans text-3xl text-ink mb-2" style={{ fontWeight: 600 }}>
            Reset your password
          </h2>
          <p className="text-sm text-ink-faint" style={{ fontWeight: 300 }}>
            Enter your email and we&apos;ll send you a reset link
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-[11px] font-sans text-ink-faint mb-1.5 tracking-wider uppercase"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className="input"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="text-xs text-rose bg-rose-faint p-3 rounded-lg font-sans">{error}</div>
          )}

          <button type="submit" disabled={loading || !email} className="btn-primary w-full">
            {loading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>

        <p className="text-center mt-6 text-xs text-ink-faint font-sans">
          Remember your password?{' '}
          <Link to="/" className="text-rose hover:underline font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
